import { Presale, PresaleSubmission, PresaleCode } from './presaleTypes';
import {
  AvailablePhoneNumber,
  DicePhoneNumber,
  DiceSMSLog,
  DiceSubscription,
  ExternalAccount,
  ExternalPlatform,
  NotificationSettings,
  WeatherReportSendResult,
  WeatherReportSubscription,
  Promoter,
  Subscription,
  SubscriptionType,
  Ticket,
  TicketsSummary,
  UserProfile,
  ProfileUpdate,
  Venue,
  SoldoutEvent,
  UpcomingSaleEvent,
  UpcomingSalesParams,
  OtpChannel,
  ReconnectDiceOtpResponse,
  JoinWaitlistRequest,
  ClaimWaitlistCodeRequest,
  ClaimWaitlistCodeResponse,
  WaitlistEntriesParams,
  WaitlistEntriesResponse,
  WaitlistEventStatusResponse,
  BulkJoinWaitlistRequest,
  BulkJoinResponse,
  BulkJoinJob,
  BulkDiceJob,
  FlashtoolsPlan,
  FlashtoolsEntitlement,
  FlashtoolsUsageDetail,
  CheckoutResponse,
  PortalResponse,
  UpcomingInvoice,
  InvoiceHistoryItem,
  ChangePlanResponse,
  TrialActionResponse,
  SyncAllResponse,
  SyncAccountResponse,
  TransferLogResponse,
  DuplicateCheckRequest,
  DuplicateCheckResponse,
  SeatListResponse,
  CreateSeatResponse,
  SupportRequest,
  SupportRequestPayload,
  AccountGroup,
  AccountGroupPayload,
  AccountGroupFlashBuyPayload,
  AccountGroupFlashBuyResult,
} from '../types';
import {
  mockAccounts,
  mockPromoters,
  mockSubscriptions,
  mockTickets,
  mockVenues,
} from './mock';
import { SyncFailedError } from './syncErrors';
import { auth } from '../firebase/config';
import { addBreadcrumb, captureError, logWarn } from '../services/sentry';
import { User, signOut } from 'firebase/auth';
import { API_V1_BASE_URL, FLASHTOOLS_BASE_URL } from './baseUrls';
import {
  endExpiredAdminSession,
  getAdminAccessToken,
  isAdminSession,
  isTokenNotValid,
  refreshAdminAccessToken,
  tokenExpiresSoon,
} from './adminSession';

const USE_MOCK = false;
// const BASE_URL = 'http://localhost:8888/api/v1/flashtools';
const BASE_URL = FLASHTOOLS_BASE_URL;
// const BASE_URL = process.env.REACT_APP_BASE_SERVER_URL || 'http://localhost:8889/api/v1/flashtools';
// Base URL for integrations (connections and transfers) - no /flashtools suffix
const INTEGRATIONS_BASE_URL = API_V1_BASE_URL;


/**
 * Collapse ids out of a URL so Sentry groups /accounts/1/sync and
 * /accounts/2/sync as one thing rather than one issue per account. Also drops
 * the query string, which can carry tokens.
 */
function routeLabel(url: string): string {
  try {
    const { pathname } = new URL(url, window.location.origin);
    return pathname.replace(/\/\d+(?=\/|$)/g, '/:id');
  } catch {
    return url.split('?')[0];
  }
}

async function getToken(): Promise<string> {
  // Check for admin session first
  if (isAdminSession()) {
    const adminToken = getAdminAccessToken();
    if (adminToken) {
      // Renew before the access token lapses, so the common case never sends
      // a dead token at all. `authenticatedFetch` still handles the race
      // where it expires between here and the server.
      if (!tokenExpiresSoon(adminToken)) return adminToken;
      const renewed = await refreshAdminAccessToken();
      if (renewed) return renewed;
      endExpiredAdminSession();
      throw Object.assign(new Error('Admin session expired'), { status: 401 });
    }
  }

  // Wait for auth to be ready if needed
  let user: User | null = auth.currentUser;

  if (!user) {
    // Wait for auth state to initialize (max 5 seconds)
    user = await new Promise<User | null>((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((u) => {
        unsubscribe();
        resolve(u);
      });
      // Timeout after 5 seconds
      setTimeout(() => resolve(null), 5000);
    });
  }

  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  console.debug('Firebase token loaded:', {
    length: token.length,
    preview: `${token.substring(0, 20)}...${token.substring(token.length - 20)}`,
  });
  return token;
}

async function getHeaders(includeContentType: boolean = false): Promise<HeadersInit> {
  const token = await getToken();
  const headers: HeadersInit = {
    'accept': '*/*',
    'authorization': `Bearer ${token}`,
  };

  if (includeContentType) {
    headers['content-type'] = 'application/json';
  }

  return headers;
}

let mockNotificationSettings: NotificationSettings = {
  new_event_notifications_enabled: true,
  ticket_change_notifications_enabled: true,
  email_notifications_enabled: true,
  notification_email: '',
};

let mockWeatherReport: WeatherReportSubscription = { subscribed: false, subscribed_at: null };

let mockSubscriptionsState: Subscription[] = [...mockSubscriptions];

// Helper function to check for access disabled error and log out user
async function handleAccessDisabledError(response: Response): Promise<void> {
  if (response.status === 403) {
    try {
      const clone = response.clone();
      const data = await clone.json();
      if (data.detail && data.detail.includes('Flashtools access is disabled')) {
        // Set flag in localStorage immediately to prevent flash on next load
        localStorage.setItem('flashtools_access_disabled', 'true');
        // Log out the user
        await signOut(auth);
        // Redirect to login with error message
        window.location.href = '/login?error=access_disabled';
        throw new Error('Your Flashtools access has been disabled. Please contact support.');
      }
    } catch (error) {
      // If it's already our thrown error, re-throw it
      if (error instanceof Error && error.message.includes('Flashtools access has been disabled')) {
        throw error;
      }
      // Otherwise, ignore JSON parse errors and continue
    }
  }
}

/** Body of a response as JSON, without consuming it; null when it is not JSON. */
async function readJsonSafely(response: Response): Promise<unknown> {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

// Wrapper for fetch that checks for access disabled errors
async function authenticatedFetch(url: string, options?: RequestInit): Promise<Response> {
  const route = routeLabel(url);
  const method = options?.method || 'GET';
  const startedAt = Date.now();

  let response: Response;
  try {
    response = await fetch(url, { ...options, credentials: 'include' });
  } catch (error) {
    // The request never completed — offline, DNS, CORS, cancelled. Worth
    // reporting: the caller usually turns this into a generic toast that tells
    // us nothing.
    captureError(error, { where: 'authenticatedFetch', route, method });
    throw error;
  }

  if (!response.ok) {
    // Only a breadcrumb. A 4xx here is usually the API correctly refusing
    // something, and the caller decides whether it is worth an issue. What
    // matters is that it shows up in the trail before whatever fails next.
    addBreadcrumb(
      'http',
      `${method} ${route} -> ${response.status}`,
      { status: response.status, durationMs: Date.now() - startedAt },
      response.status >= 500 ? 'error' : 'warning',
    );

    // An admin session whose access token just expired. Renew it and replay
    // the request once; if it cannot be renewed the session is over and the
    // admin is sent back to login instead of every query failing forever.
    // Both 401 and 403 are checked because the API historically answered an
    // expired token with 403 (see FirebaseAuthentication.authenticate_header).
    if ((response.status === 401 || response.status === 403) && isAdminSession()) {
      const rejection = await readJsonSafely(response);
      if (isTokenNotValid(rejection)) {
        const renewed = await refreshAdminAccessToken();
        if (!renewed) {
          endExpiredAdminSession();
          throw Object.assign(new Error('Admin session expired'), { status: 401 });
        }
        const headers = new Headers(options?.headers);
        headers.set('authorization', `Bearer ${renewed}`);
        addBreadcrumb('http', `${method} ${route} replayed with renewed admin token`);
        return authenticatedFetch(url, { ...options, headers });
      }
    }

    // 5xx is ours, and no caller can act on it. Log it so it is searchable
    // even when the UI swallowed the error into a toast.
    if (response.status >= 500) {
      logWarn('API returned a server error', { route, method, status: response.status });
    }
  }

  await handleAccessDisabledError(response);
  return response;
}

/**
 * Thrown when an action needs a card Stripe can actually charge and there isn't
 * one on file. `url`, when present, is a Stripe portal session that collects a
 * card and sets it as the customer's default before returning the user here —
 * so callers should navigate to it rather than surface `message` as a toast.
 */
export class PaymentMethodRequiredError extends Error {
  readonly url?: string;

  constructor(message: string, url?: string) {
    super(message);
    this.name = 'PaymentMethodRequiredError';
    this.url = url;
  }
}

/**
 * A claim that didn't go through. `kind` is what the UI branches on, because
 * the four reasons need four different answers — and `message` is Dice's own
 * words whenever the backend passed them along, which is most of the time.
 */
export class WaitlistClaimError extends Error {
  readonly status: number;
  readonly kind: 'validation' | 'dice_auth' | 'plan' | 'rejected' | 'server';

  constructor(message: string, status: number) {
    super(message);
    this.name = 'WaitlistClaimError';
    this.status = status;
    this.kind =
      status === 400 ? 'validation'
      : status === 401 ? 'dice_auth'
      : status === 403 ? 'plan'
      : status >= 500 ? 'server'
      : 'rejected';
  }
}

/**
 * AXS texts the seller a code before it will authorize a transfer, so a manual
 * transfer of AXS tickets answers 202 the first time: nothing has moved yet,
 * and the same request has to be repeated with `otp_session` + `otp_code`.
 * Every other platform answers 200 on the first call as it always did.
 */
export interface ManualTransferOtpChallenge {
  otp_required: true;
  otp_session: string;
  phone_number?: string;
  message?: string;
}

export interface ManualTransferCompleted {
  otp_required?: false;
  success: boolean;
  message: string;
  platform: string;
  event_id: string;
  ticket_ids: string[];
  recipient: string;
  transfer_result: any;
}

export type ManualTransferResult = ManualTransferCompleted | ManualTransferOtpChallenge;

export const isManualTransferOtpChallenge = (
  result: ManualTransferResult,
): result is ManualTransferOtpChallenge => result.otp_required === true;

/**
 * The code was refused. Either way the session is spent — AXS burns it on the
 * attempt, right or wrong — so there is no retrying the same code and never an
 * automatic retry: the next attempt needs a code the seller asks for again.
 *
 *   otp_invalid          wrong code typed; stay on the prompt, resend
 *   otp_session_invalid  session expired (10 min) or already used; start over
 */
export class ManualTransferOtpError extends Error {
  readonly code: 'otp_invalid' | 'otp_session_invalid';

  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = 'ManualTransferOtpError';
    this.code = code === 'otp_session_invalid' ? 'otp_session_invalid' : 'otp_invalid';
  }
}

/**
 * Password reset runs before the user has any credentials, so these three calls
 * deliberately skip getHeaders()/authenticatedFetch — there is no token to send
 * and no session to invalidate on 403.
 */
async function publicPost<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'accept': 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    // The request never reached the server — offline, DNS, CORS. The caller
    // turns this into a generic toast, so without reporting it here a user
    // who cannot reset their password is invisible to us.
    captureError(error, { where: 'publicPost', path, reason: 'network' });
    throw error;
  }

  let data: any = null;
  try {
    data = await res.json();
  } catch {
    // Throttle responses and gateway errors can arrive without a JSON body.
  }

  if (!res.ok) {
    // Never attach the body: these endpoints carry reset codes and tokens.
    addBreadcrumb(
      'http',
      `POST ${path} -> ${res.status}`,
      { status: res.status },
      res.status >= 500 ? 'error' : 'warning',
    );

    if (res.status === 429) {
      throw new Error('Too many attempts. Wait a few minutes and try again.');
    }
    if (res.status >= 500) {
      captureError(new Error(`Public endpoint failed: POST ${path} -> ${res.status}`), {
        where: 'publicPost',
        path,
        status: res.status,
      });
    }
    throw new Error(data?.error || data?.detail || 'Something went wrong. Please try again.');
  }

  return data as T;
}

/** Presale writes are never retried after transport or upstream failures. */
async function presaleRequest<T>(path: string, method = 'GET', payload?: PresaleSubmission): Promise<T> {
  const response = await authenticatedFetch(`${BASE_URL}/${path}`, {
    method,
    headers: await getHeaders(payload !== undefined),
    cache: 'no-store',
    ...(payload ? { body: JSON.stringify(payload) } : {}),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok || body?.success === false) {
    throw Object.assign(new Error(body?.error || body?.detail || `Presale request failed: ${response.status}`), {
      status: response.status, code: body?.code,
    });
  }
  if (method === 'DELETE' && !path.endsWith('/upvote')) {
    if (body?.success !== true) throw new Error('Deletion could not be confirmed. Refresh before retrying.');
    return undefined as T;
  }
  if (!body || !('data' in body)) throw new Error('Invalid presale response. Refresh before trying again.');
  return body.data as T;
}

/** A bulk-join job the API will no longer cancel: it has already ended. */
export function isFinishedJobStatus(status: unknown): status is BulkJoinJob['status'] {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

export const api = {
  deletePresaleCode: (codeId: number) => presaleRequest<void>(`presale-codes/${codeId}`, 'DELETE'),
  getPresales: () => presaleRequest<Presale[]>('presales?status=all&limit=200'),
  submitEventPresaleCode: (eventId: number, payload: PresaleSubmission) =>
    presaleRequest<{ was_duplicate: boolean }>(`events/${eventId}/presale-codes`, 'POST', payload),
  votePresaleCode: (codeId: number, withdraw: boolean) =>
    presaleRequest<Pick<PresaleCode, 'upvote_count' | 'viewer_has_upvoted'>>(
      `presale-codes/${codeId}/upvote`, withdraw ? 'DELETE' : 'POST'),

  /**
   * Ask for a reset code. Always resolves, even for an unknown address — the
   * backend answers identically either way so the response cannot be used to
   * discover which emails have accounts. Do not "improve" this by surfacing a
   * not-found state.
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return publicPost('/auth/password-reset/request', { email });
  },

  /** Trade the emailed code for a single-use reset token. */
  async verifyPasswordResetCode(
    email: string,
    code: string,
  ): Promise<{ reset_token: string; expires_in: number }> {
    return publicPost('/auth/password-reset/verify', { email, code });
  },

  /** Set the new password. Returns the email so the caller can sign in. */
  async confirmPasswordReset(
    resetToken: string,
    password: string,
    confirmPassword: string,
  ): Promise<{ message: string; email: string }> {
    return publicPost('/auth/password-reset/confirm', {
      reset_token: resetToken,
      password,
      confirm_password: confirmPassword,
    });
  },

  async getProfile(): Promise<UserProfile> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/users/profile/`, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch profile: ${res.status} ${res.statusText}`);
    }

    return res.json();
  },

  async updateProfile(data: ProfileUpdate): Promise<UserProfile> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/users/profile/`, {
      method: 'PATCH',
      headers: await getHeaders(true),
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      const text = await res.text();
      let message = `Failed to update profile: ${res.status} ${res.statusText}`;
      try {
        const j = JSON.parse(text);
        message = j.detail || j.message || j.error || (Array.isArray(j) ? j[0] : message);
      } catch {
        message = text || message;
      }
      throw new Error(message);
    }

    return res.json();
  },

  async getNotificationSettings(): Promise<NotificationSettings> {
    if (USE_MOCK) return Promise.resolve(mockNotificationSettings);

    const res = await authenticatedFetch(`${BASE_URL}/notifications/settings`, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch notification settings: ${res.status} ${res.statusText}`);
    }

    const json = await res.json();
    return json.data || json;
  },

  async updateNotificationSettings(
    settings: Partial<NotificationSettings>
  ): Promise<NotificationSettings> {
    if (USE_MOCK) {
      mockNotificationSettings = { ...mockNotificationSettings, ...settings };
      return Promise.resolve(mockNotificationSettings);
    }

    const res = await authenticatedFetch(`${BASE_URL}/notifications/settings`, {
      method: 'PUT',
      headers: await getHeaders(true),
      body: JSON.stringify(settings),
    });

    if (!res.ok) {
      throw new Error(`Failed to update notification settings: ${res.status} ${res.statusText}`);
    }

    const json = await res.json();
    return json.data || json;
  },

  async getWeatherReportSubscription(): Promise<WeatherReportSubscription> {
    if (USE_MOCK) return Promise.resolve(mockWeatherReport);

    const res = await authenticatedFetch(`${BASE_URL}/notifications/weather-report`, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch Flash Forecast: ${res.status} ${res.statusText}`);
    }

    const json = await res.json();
    return json.data || json;
  },

  /** PUT subscribes, DELETE unsubscribes. Both are idempotent. */
  async setWeatherReportSubscription(subscribed: boolean): Promise<WeatherReportSubscription> {
    if (USE_MOCK) {
      mockWeatherReport = { subscribed, subscribed_at: subscribed ? new Date().toISOString() : null };
      return Promise.resolve(mockWeatherReport);
    }

    const res = await authenticatedFetch(`${BASE_URL}/notifications/weather-report`, {
      method: subscribed ? 'PUT' : 'DELETE',
      headers: await getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to update Flash Forecast: ${res.status} ${res.statusText}`);
    }

    const json = await res.json();
    return json.data || json;
  },

  /**
   * Emails a Flash Forecast now, whether or not the user is subscribed.
   * Rate limited to one send per 10 minutes (429). Errors carry `status`.
   */
  async sendWeatherReportNow(): Promise<WeatherReportSendResult> {
    if (USE_MOCK) {
      return Promise.resolve({ sent: true, message: 'Flash Forecast sent to you@example.com' });
    }

    const res = await authenticatedFetch(`${BASE_URL}/notifications/weather-report/send`, {
      method: 'POST',
      headers: await getHeaders(),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw Object.assign(
        new Error(json.error || `Failed to send Flash Forecast: ${res.status} ${res.statusText}`),
        { status: res.status },
      );
    }

    return { sent: json.data?.sent ?? true, message: json.message || 'Flash Forecast sent' };
  },

  async getVenues(params?: {
    city?: string;
    popular?: boolean;
    limit?: number;
    all?: boolean;
  }): Promise<Venue[]> {
    if (USE_MOCK) return Promise.resolve(mockVenues);

    const queryParams = new URLSearchParams();
    if (params?.city) queryParams.append('city', params.city);
    if (params?.popular) queryParams.append('popular', 'true');
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.all) queryParams.append('all', 'true');

    const url = queryParams.toString()
      ? `${BASE_URL}/venues?${queryParams.toString()}`
      : `${BASE_URL}/venues`;

    const res = await authenticatedFetch(url, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error('Failed to fetch venues:', {
        status: res.status,
        statusText: res.statusText,
        response: errorText,
        headers: Object.fromEntries(res.headers.entries()),
      });
      throw new Error(`Failed to fetch venues: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();
    return data.data || data.venues || [];
  },

  async getPromoters(params?: {
    popular?: boolean;
    limit?: number;
  }): Promise<Promoter[]> {
    if (USE_MOCK) return Promise.resolve(mockPromoters);

    const queryParams = new URLSearchParams();
    if (params?.popular) queryParams.append('popular', 'true');
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const url = queryParams.toString()
      ? `${BASE_URL}/promoters?${queryParams.toString()}`
      : `${BASE_URL}/promoters`;

    const res = await authenticatedFetch(url, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch promoters: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();
    return data.data || data.promoters || [];
  },

  async requestVenue(name: string, city?: string, notes?: string, platform?: string, url?: string) {
    const body: Record<string, any> = { name };
    if (city) body.city = city;
    if (notes) body.additional_info = notes;
    if (platform) body.platform = platform;
    if (url) body.url = url;
    const res = await authenticatedFetch(`${BASE_URL}/requests/venue`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to submit venue request: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.json();
  },

  async requestPromoter(name: string, notes?: string, platform?: string, url?: string) {
    const body: Record<string, any> = { name };
    if (notes) body.additional_info = notes;
    if (platform) body.platform = platform;
    if (url) body.url = url;
    const res = await authenticatedFetch(`${BASE_URL}/requests/promoter`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to submit promoter request: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.json();
  },

  // ── Support / bug requests ──────────────────────────────────────────────

  /**
   * File a support or bug request. Not gated on a subscription: the backend
   * takes IsAuthenticated only, so a lapsed account can still reach support.
   */
  async submitSupportRequest(payload: SupportRequestPayload): Promise<{ message: string; data: SupportRequest }> {
    const res = await authenticatedFetch(`${BASE_URL}/support/requests`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to submit support request: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorJson.message || errorJson.detail || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw Object.assign(new Error(errorMessage), { status: res.status });
    }

    return res.json();
  },

  async getSupportRequests(): Promise<SupportRequest[]> {
    const res = await authenticatedFetch(`${BASE_URL}/support/requests`, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch support requests: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();
    return data.data || [];
  },

  async requestArtist(name: string, notes?: string, platform?: string, url?: string) {
    const body: Record<string, any> = { name };
    if (notes) body.additional_info = notes;
    if (platform) body.platform = platform;
    if (url) body.url = url;
    const res = await authenticatedFetch(`${BASE_URL}/requests/artist`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to submit artist request: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.json();
  },

  async subscribe(type: SubscriptionType, entityId: string) {
    if (USE_MOCK) {
      let entityName = 'Unknown';
      if (type === 'venue') {
        const venue = mockVenues.find((v) => v.venue_id === entityId);
        entityName = venue?.name || 'Unknown';
      } else if (type === 'promoter') {
        const promoter = mockPromoters.find((p) => p.promoter_id === entityId);
        entityName = promoter?.name || 'Unknown';
      }

      const subscriptionId = Math.floor(Math.random() * 1000) + 1000; 
      const newSubscription: Subscription = {
        id: subscriptionId,
        type,
        entity_id: entityId,
        entity_name: entityName,
        entity_details: {},
        created_at: new Date().toISOString(),
      };

      mockSubscriptionsState = [...mockSubscriptionsState, newSubscription];

      return Promise.resolve({
        subscription_id: subscriptionId,
        type,
        entity_id: entityId,
        entity_name: entityName,
        message: `Subscribed to ${entityName}`,
      });
    }

    const res = await authenticatedFetch(`${BASE_URL}/notifications/subscribe`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ type, entity_id: entityId }),
    });
    return res.json();
  },

  async getSubscriptions(): Promise<Subscription[]> {
    if (USE_MOCK) return Promise.resolve(mockSubscriptionsState);

    const res = await authenticatedFetch(`${BASE_URL}/notifications/subscriptions`, {
      headers: await getHeaders(),
    });
    const data = await res.json();

    // API returns { data: { venues: [], artists: [], promoters: [] } }
    // Flatten into a single array
    if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
      const { venues = [], artists = [], promoters = [] } = data.data;
      return [
        ...venues.map((v: any) => ({ ...v, type: 'venue' as const })),
        ...artists.map((a: any) => ({ ...a, type: 'artist' as const })),
        ...promoters.map((p: any) => ({ ...p, type: 'promoter' as const })),
      ];
    }

    return data.data || data.subscriptions || [];
  },

  async unsubscribe(id: number, type: SubscriptionType) {
    if (USE_MOCK) {
      // Remove from mock state
      mockSubscriptionsState = mockSubscriptionsState.filter((sub) => sub.id !== id);
      return Promise.resolve({ message: 'Unsubscribed' });
    }

    const typePathMap: Record<SubscriptionType, string> = {
      venue: 'venues',
      artist: 'artists',
      promoter: 'promoters',
    };

    const res = await authenticatedFetch(`${BASE_URL}/notifications/subscriptions/${typePathMap[type]}/${id}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    });
    return res.json();
  },

  async getAccounts(params?: { platform?: string }): Promise<ExternalAccount[]> {
    if (USE_MOCK) return Promise.resolve(mockAccounts);

    const queryParams = new URLSearchParams();
    if (params?.platform) queryParams.append('platform', params.platform);

    const url = queryParams.toString()
      ? `${BASE_URL}/accounts?${queryParams.toString()}`
      : `${BASE_URL}/accounts`;

    const res = await authenticatedFetch(url, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to fetch accounts: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await res.json();
    const accounts = data.data || data.accounts || [];

    // [DEBUG accounts-source] Temporary: confirm which identity + endpoint this
    // list came from, to compare against the mobile app. Remove when resolved.
    const adminSession = isAdminSession();
    console.log('[DEBUG accounts-source] Flashtools UI getAccounts', {
      endpoint: url,
      authMode: adminSession ? 'admin-session-token' : 'firebase',
      uid: auth.currentUser?.uid ?? null,
      email: auth.currentUser?.email ?? null,
      count: accounts.length,
      ids: accounts.map((a: ExternalAccount) => `${a.id}:${a.platform}:${a.email || a.phone_masked || ''}`),
      raw: data,
    });

    return accounts;
  },

  async sendOTP(phone: string, platform: string, channel: string = 'sms'): Promise<{ device_id: string }> {
    if (USE_MOCK) {
      return Promise.resolve({ device_id: 'mock-device-id' });
    }

    const url = `${INTEGRATIONS_BASE_URL}/integrations/otp/`;
    console.log('Sending OTP:', { url, phone, platform, channel });

    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ phone, platform, channel }),
    });

    console.log('OTP response status:', res.status, res.statusText);

    if (!res.ok) {
      const errorText = await res.text();
      console.error('OTP error response:', errorText);
      let errorMessage = `Failed to send OTP: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.json();
  },

  async connectWithOTP(phone: string, otp: string, platform: string, deviceId: string) {
    if (USE_MOCK) {
      return Promise.resolve({
        id: 789,
        platform: 'DICE' as ExternalPlatform,
        email: 'user@example.com',
        connected_at: new Date().toISOString(),
        message: 'Account connected successfully',
      });
    }

    const url = `${INTEGRATIONS_BASE_URL}/integrations/external-connections/`;
    console.log('Connecting with OTP:', { url, phone, platform, deviceId });

    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ phone, otp, platform, device_id: deviceId }),
    });

    console.log('Connect response status:', res.status, res.statusText);

    if (!res.ok) {
      const errorText = await res.text();
      console.error('Connect error response:', errorText);
      let errorMessage = `Failed to connect account: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.json();
  },

  // Reconnect an existing DICE account: the server already has the phone (encrypted),
  // so we just pass the connection id and it fires the OTP. The browser never sees
  // or re-types the number.
  async reconnectDiceSendOtp(
    connectionId: number,
    channel: OtpChannel = 'sms',
  ): Promise<ReconnectDiceOtpResponse> {
    if (USE_MOCK) {
      return Promise.resolve({
        device_id: 'mock-device-id',
        phone_masked: '***9090',
        channel,
        sent_to_display: channel === 'email' ? 'user@example.com' : '***9090',
        available_channels: ['sms', 'email', 'call'],
      });
    }

    const url = `${INTEGRATIONS_BASE_URL}/integrations/reconnect/dice/send-otp/`;
    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ connection_id: connectionId, channel }),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to send OTP: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        // Validation failures are DRF field errors — {"channel": ["..."]} —
        // not the {"error": "..."} envelope the other branches use.
        const fieldError = Object.values(errorJson).find(
          (v) => Array.isArray(v) && typeof v[0] === 'string',
        ) as string[] | undefined;
        errorMessage = errorJson.message || errorJson.error || fieldError?.[0] || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.json();
  },

  async reconnectDiceVerifyOtp(connectionId: number, otp: string, deviceId: string) {
    if (USE_MOCK) {
      return Promise.resolve({ message: 'Account reconnected successfully' });
    }

    const url = `${INTEGRATIONS_BASE_URL}/integrations/reconnect/dice/verify-otp/`;
    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ connection_id: connectionId, otp, device_id: deviceId }),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to reconnect account: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.json();
  },

  async connectDice(phone: string, password: string) {
    if (USE_MOCK) {
      return Promise.resolve({
        id: 789,
        platform: 'DICE' as ExternalPlatform,
        email: 'user@example.com',
        connected_at: new Date().toISOString(),
        message: 'Dice account connected successfully',
      });
    }

    const url = `${BASE_URL}/accounts/dice/connect`;
    console.log('Connecting DICE account:', { url, phone });

    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ phone, password }),
    });

    console.log('DICE response status:', res.status, res.statusText);

    if (!res.ok) {
      const errorText = await res.text();
      console.error('DICE error response:', errorText);
      let errorMessage = `Failed to connect DICE account: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    let data;
    try {
      const text = await res.text();
      console.log('DICE response text:', text);
      data = text ? JSON.parse(text) : {};
      console.log('DICE connection success:', data);
    } catch (parseError) {
      console.error('DICE response parse error:', parseError);
      throw new Error('Invalid response from server');
    }
    return data;
  },

  // AXS connect is two-step. Mirrors the mobile app: same polymorphic
  // `/integrations/connect/` endpoint, distinguished by `step: '1' | '2'`.
  async sendAxsOtp(email: string, password: string): Promise<{
    success: boolean;
    message?: string;
    mfa_request_token: string;
    login_hash: string;
    browser_session?: boolean;
    email: string;
    error?: string;
  }> {
    if (USE_MOCK) {
      return Promise.resolve({
        success: true,
        message: 'OTP sent (mock).',
        mfa_request_token: 'mock-mfa-token',
        login_hash: 'mock-login-hash',
        browser_session: false,
        email,
      });
    }

    const url = `${INTEGRATIONS_BASE_URL}/integrations/connect/`;
    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ platform: 'axs', step: '1', email, password }),
    });

    const text = await res.text();
    let data: any;
    try { data = text ? JSON.parse(text) : {}; } catch { data = { error: text }; }

    if (!res.ok || data?.success === false) {
      throw new Error(data?.error || data?.message || `Failed to send AXS OTP (${res.status})`);
    }
    return { ...data, email: data?.email || email };
  },

  async verifyAxsOtp(params: {
    email: string;
    otp: string;
    mfaRequestToken: string;
    loginHash?: string;
    browserSession?: boolean;
  }) {
    if (USE_MOCK) {
      return Promise.resolve({
        success: true,
        connection_id: 790,
        email: params.email,
        message: 'AXS account connected successfully',
      });
    }

    const url = `${INTEGRATIONS_BASE_URL}/integrations/connect/`;
    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        platform: 'axs',
        step: '2',
        email: params.email,
        otp: params.otp,
        mfa_request_token: params.mfaRequestToken,
        login_hash: params.loginHash || '',
      }),
    });

    const text = await res.text();
    let data: any;
    try { data = text ? JSON.parse(text) : {}; } catch { data = { error: text }; }

    if (!res.ok || data?.success === false) {
      throw new Error(data?.error || data?.message || `Failed to verify AXS OTP (${res.status})`);
    }
    return data;
  },

  // TAO connect mirrors the mobile app: POST to the polymorphic
  // /integrations/connect/ endpoint with platform:'tao'. The card_number
  // arg is accepted for backward compatibility with the UI form but ignored
  // — the backend reads the real printedCardNumber from TAO's login response.
  async connectTAO(email: string, password: string, _card_number: string) {
    if (USE_MOCK) {
      return Promise.resolve({
        id: 791,
        platform: 'TAO' as ExternalPlatform,
        email,
        card_number_masked: '****7890',
        connected_at: new Date().toISOString(),
        message: 'TAO account connected successfully',
      });
    }

    const url = `${INTEGRATIONS_BASE_URL}/integrations/connect/`;
    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ platform: 'tao', step: '1', email, password }),
    });

    const text = await res.text();
    let data: any;
    try { data = text ? JSON.parse(text) : {}; } catch { data = { error: text }; }

    if (!res.ok || data?.success === false) {
      throw new Error(data?.error || data?.message || `Failed to connect TAO account (${res.status})`);
    }
    return data;
  },

  async disconnectAccount(id: number) {
    if (USE_MOCK) {
      return Promise.resolve({ message: 'Account disconnected successfully' });
    }

    const res = await authenticatedFetch(`${BASE_URL}/accounts/${id}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to disconnect account: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    return res.json();
  },

  async syncAccount(id: number): Promise<SyncAccountResponse> {
    if (USE_MOCK) {
      return Promise.resolve({
        success: true,
        message: 'Tickets synced successfully',
      });
    }

    const res = await authenticatedFetch(`${BASE_URL}/accounts/${id}/sync`, {
      method: 'POST',
      headers: await getHeaders(),
    });
    const data: SyncAccountResponse | null = await res.json().catch(() => null);

    // A failed sync reports itself in the body, and the status it arrives with
    // depends on the cause — 503 for proxy_blocked, 400 for network/forbidden,
    // 401 for banned and the auth codes. Branch on the body, not the status.
    //
    // Without this the whole failure body resolved like a success, and callers
    // toasted `message` green — including "DICE blocked the proxy exit node
    // for +1626… " on a 503.
    if (!res.ok || data?.success === false) {
      throw new SyncFailedError(data?.error_code ?? null, data?.message, res.status);
    }

    return data ?? { success: true };
  },

  // Returns the Stripe publishable key the browser needs to tokenize a card
  // with Stripe.js (the raw card never touches our server).
  async getDiceCardSetup(id: number): Promise<{ success: boolean; publishable_key?: string; error?: string }> {
    const res = await authenticatedFetch(`${BASE_URL}/accounts/${id}/card-setup`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    return res.json();
  },

  // Attach a browser-tokenized card (pm_...) to the DICE account.
  async addDiceCard(id: number, paymentMethodId: string): Promise<{ success: boolean; saved_cards?: import('../types').SavedCard[]; error?: string }> {
    const res = await authenticatedFetch(`${BASE_URL}/accounts/${id}/add-card`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ payment_method_id: paymentMethodId }),
    });
    return res.json();
  },

  async syncAllAccounts(): Promise<SyncAllResponse> {
    if (USE_MOCK) {
      return Promise.resolve({
        success: true,
        message: '2 synced, 0 failed',
        total_synced: 5,
        succeeded: 2,
        failed: 0,
        results: [],
      });
    }

    const res = await authenticatedFetch(`${BASE_URL}/accounts/sync-all`, {
      method: 'POST',
      headers: await getHeaders(),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err: any = new Error(body.message || body.error || `Sync failed (${res.status})`);
      err.status = res.status;
      err.body = body;
      throw err;
    }
    return body;
  },

  // ── Account groups ────────────────────────────────────────────────────────
  // Tags on connected accounts with a standing Flash Buy rule. Reads are open
  // to a team member; every write is owner-only (403 for a seat).

  async getAccountGroups(): Promise<AccountGroup[]> {
    const res = await authenticatedFetch(`${BASE_URL}/groups`, { headers: await getHeaders() });
    if (!res.ok) {
      throw Object.assign(new Error(`Failed to load groups: ${res.status}`), { status: res.status });
    }
    const data = await res.json();
    return data.groups ?? [];
  },

  async createAccountGroup(payload: AccountGroupPayload): Promise<AccountGroup> {
    const res = await authenticatedFetch(`${BASE_URL}/groups`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw Object.assign(new Error(err.error || err.detail || 'Could not create the group'), { status: res.status });
    }
    return res.json();
  },

  /** Rename, recolour, or change the standing rule. A rule change is applied
   *  to the members immediately; the response carries `applied` when it was. */
  async updateAccountGroup(
    id: number,
    payload: AccountGroupPayload,
  ): Promise<AccountGroup & { applied?: AccountGroupFlashBuyResult }> {
    const res = await authenticatedFetch(`${BASE_URL}/groups/${id}`, {
      method: 'PATCH',
      headers: await getHeaders(true),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw Object.assign(new Error(err.error || err.detail || 'Could not update the group'), { status: res.status });
    }
    return res.json();
  },

  async deleteAccountGroup(id: number): Promise<{ success: boolean; id: number }> {
    const res = await authenticatedFetch(`${BASE_URL}/groups/${id}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw Object.assign(new Error(err.error || err.detail || 'Could not delete the group'), { status: res.status });
    }
    return res.json();
  },

  /** Added accounts inherit the group's standing rule on the spot. */
  async updateAccountGroupMembers(
    id: number,
    changes: { add?: number[]; remove?: number[] },
  ): Promise<AccountGroup & { inherited: { applied: number; tier_updated: number } }> {
    const res = await authenticatedFetch(`${BASE_URL}/groups/${id}/members`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(changes),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw Object.assign(new Error(err.error || err.detail || 'Could not update the group'), { status: res.status });
    }
    return res.json();
  },

  /** Apply a Flash Buy action to a group's eligible members and keep it as the rule. */
  async applyAccountGroupFlashBuy(
    id: number,
    payload: AccountGroupFlashBuyPayload,
  ): Promise<AccountGroupFlashBuyResult> {
    const res = await authenticatedFetch(`${BASE_URL}/groups/${id}/flash-buy`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw Object.assign(new Error(err.error || err.detail || 'Could not update Flash Buy for this group'), { status: res.status });
    }
    return res.json();
  },

  async getTransferLog(params?: {
    source?: string;
    /** Owner only. 'team' = sends by any seat member, 'me' = the owner's own. */
    sent_by?: 'team' | 'me';
    /** Owner only. Narrow to one seat member's sends. */
    seat_id?: number;
    status?: string;
    platform?: string;
    account_id?: number;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<TransferLogResponse> {
    if (USE_MOCK) {
      return Promise.resolve({
        transfers: [],
        pagination: { total: 0, limit: 50, offset: 0, has_more: false },
      });
    }
    const qs = new URLSearchParams();
    if (params?.source) qs.set('source', params.source);
    if (params?.sent_by) qs.set('sent_by', params.sent_by);
    if (params?.seat_id != null) qs.set('seat_id', String(params.seat_id));
    if (params?.status) qs.set('status', params.status);
    if (params?.platform) qs.set('platform', params.platform);
    if (params?.account_id != null) qs.set('account_id', String(params.account_id));
    if (params?.search) qs.set('search', params.search);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const qstr = qs.toString();
    const res = await authenticatedFetch(`${BASE_URL}/transfers${qstr ? `?${qstr}` : ''}`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    return res.json();
  },

  async checkDuplicateTransfer(payload: DuplicateCheckRequest): Promise<DuplicateCheckResponse> {
    if (USE_MOCK) {
      return Promise.resolve({ strict_matches: [], loose_matches: [], lookback_days: 7 });
    }
    const res = await authenticatedFetch(`${BASE_URL}/transfers/check-duplicate`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async getTickets(params?: {
    status?: string;
    platform?: string;
    date_from?: string;
    date_to?: string;
    group_by?: 'event';
    include_past?: boolean;
    /** Only tickets on blocked accounts that are still connected (the review view). */
    banned?: boolean;
  }): Promise<{
    tickets: Ticket[];
    summary: TicketsSummary;
    grouped_tickets?: any[];
    /** What the server withheld, and why. Absent on older backends. */
    hidden?: { banned_tickets: number; banned_accounts: number };
  }> {
    if (USE_MOCK) {
      return Promise.resolve({
        tickets: mockTickets,
        summary: {
          total_tickets: 2,
          active_tickets: 2,
          upcoming_events: 2,
        },
      });
    }

    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.platform) queryParams.append('platform', params.platform);
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.group_by) queryParams.append('group_by', params.group_by);
    if (params?.include_past) queryParams.append('include_past', 'true');
    if (params?.banned) queryParams.append('banned', 'true');

    const url = queryParams.toString()
      ? `${BASE_URL}/tickets?${queryParams.toString()}`
      : `${BASE_URL}/tickets`;

    const res = await authenticatedFetch(url, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      // Parsing an error page as JSON surfaces as Safari's "The string did
      // not match the expected pattern", which hides the status entirely.
      throw Object.assign(new Error(`Failed to fetch tickets: ${res.status}`), { status: res.status });
    }
    const data = await res.json();
    const tickets = data.data || data.tickets || [];
    return {
      tickets,
      summary: data.summary || {
        total_tickets: tickets.length,
        active_tickets: tickets.length,
        upcoming_events: tickets.length,
      },
      grouped_tickets: data.grouped_tickets,
      hidden: data.hidden,
    };
  },

  async transferTicket(
    ticketId: string,
    recipientEmail: string,
    recipientName: string,
    message: string
  ) {
    if (USE_MOCK) {
      return Promise.resolve({
        transfer_id: 'tr_789',
        recipient_email: recipientEmail,
        event_name: 'KAYTRANADA',
        status: 'pending',
        message: `Ticket sent to ${recipientEmail}`,
      });
    }

    const res = await authenticatedFetch(`${BASE_URL}/tickets/${ticketId}/transfer`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        message,
      }),
    });
    return res.json();
  },

  async validateGiftTransfer(
    recipientEmail: string,
    ticketIds: string[],
    quantity: number
  ): Promise<{
    recipient_name: string;
    recipient_email: string;
    platform: string;
    event_name: string;
    event_date: string;
    venue_name: string;
    ticket_count: number;
    ready_for_gift: boolean;
    gift_session_key?: string;
    validation_note?: string;
  }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/gift-ticket/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        recipient_email: recipientEmail,
        ticket_ids: ticketIds,
        quantity,
      }),
    });

    if (!res.ok) {
      const errorData = await res.json();
      // Check for recipient missing platform connection
      if (errorData.platform_required) {
        throw new Error(
          `${errorData.error}\n\n${errorData.details}\n\nRecipient: ${errorData.recipient_name}\nPlatform Required: ${errorData.platform_required}`
        );
      }
      // Check for ticket validation errors
      if (errorData.ticket_ids) {
        throw new Error(errorData.ticket_ids.join(', '));
      }
      // Check for mixed platforms
      if (errorData.non_field_errors) {
        throw new Error(errorData.non_field_errors.join(', '));
      }
      throw new Error(errorData.error || 'Validation failed');
    }

    return res.json();
  },

  async confirmGiftTransfer(
    recipientEmail: string,
    externalTicketIds: string[],
    quantity: number
  ): Promise<{
    success: boolean;
    message: string;
    gift_group_id: string;
    recipient_email: string;
    platform: string;
    tickets_transferred: number;
    event_name: string;
    audit_trail: {
      gift_group_id: string;
      created_at: string;
      status: string;
    };
  }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/gift-ticket/confirm/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        recipient_email: recipientEmail,
        external_ticket_ids: externalTicketIds,
        quantity,
        confirmation: 'GIFT',
      }),
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || errorData.details || 'Transfer failed');
    }

    return res.json();
  },

  async exportTickets(params?: {
    platform?: string;
    include_past?: boolean;
    date_from?: string;
    date_to?: string;
    status?: string;
    exclude_status?: string;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.platform) queryParams.append('platform', params.platform);
    if (params?.include_past) queryParams.append('include_past', 'true');
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.exclude_status) queryParams.append('exclude_status', params.exclude_status);

    const url = queryParams.toString()
      ? `${BASE_URL}/tickets/export?${queryParams.toString()}`
      : `${BASE_URL}/tickets/export`;

    const res = await authenticatedFetch(url, {
      method: 'GET',
      headers: await getHeaders(),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to export tickets: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.blob();
  },

  /** The ticket's entry QR as a PNG. Only DICE tickets carry one (Ticket.qr_image_url is set). */
  async getTicketQr(ticketId: number): Promise<Blob> {
    const res = await authenticatedFetch(`${BASE_URL}/tickets/${ticketId}/qr.png`, {
      method: 'GET',
      headers: await getHeaders(),
    });

    if (!res.ok) {
      let errorMessage = `Failed to load QR code: ${res.status} ${res.statusText}`;
      try {
        const errorJson = await res.json();
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        // Non-JSON error body; keep the status line.
      }
      throw new Error(errorMessage);
    }

    return res.blob();
  },

  /**
   * One endpoint, one or two calls. AXS answers the first call with 202 and an
   * `otp_session`; the caller shows the code prompt and calls this again with
   * the SAME fields plus `otpSession` + `otpCode`. It is not a separate confirm
   * endpoint, so callers keep the original params around rather than rebuild
   * them. Resending a code is just call one again, with no `otpCode`.
   */
  async manualTransfer(params: {
    platform: string;
    eventId: string;
    ticketIds: string[];
    recipientEmail?: string;
    recipientPhone?: string;
    recipientFirstName?: string;
    recipientLastName?: string;
    otpSession?: string;
    otpCode?: string;
  }): Promise<ManualTransferResult> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/manual-transfer/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({
        platform: params.platform,
        event_id: params.eventId,
        ticket_ids: params.ticketIds,
        recipient_email: params.recipientEmail,
        recipient_phone: params.recipientPhone,
        recipient_first_name: params.recipientFirstName,
        recipient_last_name: params.recipientLastName,
        ...(params.otpSession ? { otp_session: params.otpSession } : {}),
        ...(params.otpCode ? { otp_code: params.otpCode } : {}),
      }),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Failed to transfer ticket: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        // A refused code is its own error type: the UI keeps the seller on the
        // prompt for a wrong code and sends them back to the form for a dead
        // session, and neither is a generic transfer failure.
        if (errorJson.otp_required) {
          throw new ManualTransferOtpError(
            errorJson.error || errorJson.detail || 'That code was not accepted.',
            errorJson.error_code,
          );
        }
        // Handle validation errors
        if (errorJson.ticket_ids) {
          errorMessage = Array.isArray(errorJson.ticket_ids)
            ? errorJson.ticket_ids.join(', ')
            : errorJson.ticket_ids;
        } else if (errorJson.recipient_email) {
          errorMessage = Array.isArray(errorJson.recipient_email)
            ? errorJson.recipient_email.join(', ')
            : errorJson.recipient_email;
        } else if (errorJson.error) {
          errorMessage = errorJson.error;
          if (errorJson.details) {
            errorMessage += `\n${errorJson.details}`;
          }
        } else if (errorJson.detail) {
          errorMessage = errorJson.detail;
        } else {
          errorMessage = errorJson.message || errorMessage;
        }
      } catch (parseError) {
        if (parseError instanceof ManualTransferOtpError) throw parseError;
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    // 202 means the code is on its way and NOTHING has transferred yet, so the
    // shape is the challenge rather than a transfer result.
    return res.json();
  },

  // ── Batch transfer (DICE phone, multi-recipient / multi-event) ────────────

  async batchTransfer(payload: {
    platform: 'DICE' | 'TAO';
    transfers: Array<{
      event_id: string;
      recipient_phone: string;
      ticket_ids: string[];
      client_ref?: string;
    }>;
  }): Promise<{
    platform: string;
    batch_request_id: string;
    summary: {
      total: number;
      succeeded: number;
      failed: number;
      tickets_transferred: number;
    };
    results: Array<{
      client_ref: string;
      event_id: string;
      recipient_phone: string;
      ticket_ids: string[];
      success: boolean;
      transfer_group_id: string | null;
      tickets_transferred?: number;
      error?: string;
      error_type?: string;
      error_code?: number | null;
    }>;
  }> {
    const res = await authenticatedFetch(
      `${INTEGRATIONS_BASE_URL}/integrations/batch-transfer/`,
      {
        method: 'POST',
        headers: await getHeaders(true),
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      const errorText = await res.text();
      let errorMessage = `Batch transfer failed: ${res.status} ${res.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.transfers) {
          errorMessage = Array.isArray(errorJson.transfers)
            ? errorJson.transfers.join(', ')
            : JSON.stringify(errorJson.transfers);
        } else if (errorJson.detail) {
          errorMessage = errorJson.detail;
        } else if (errorJson.error) {
          errorMessage = errorJson.error;
          if (errorJson.details) errorMessage += `\n${errorJson.details}`;
        } else {
          errorMessage = errorJson.message || errorMessage;
        }
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return res.json();
  },

  // ── Listings ──────────────────────────────────────────────────────────────

  async getListings(params?: { status?: string; event_id?: number }): Promise<{ success: boolean; count: number; data: any[] }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.event_id) qs.set('event_id', String(params.event_id));
    const url = `${BASE_URL}/listings${qs.toString() ? `?${qs}` : ''}`;
    const res = await authenticatedFetch(url, { headers: await getHeaders(false) });
    if (!res.ok) throw new Error(`Failed to fetch listings: ${res.status}`);
    return res.json();
  },

  async updateListing(id: number, data: {
    price_per_ticket?: string;
    status?: 'ACTIVE' | 'INACTIVE';
    listing_notes?: string | null;
    in_hand_date?: string | null;
    section?: string | null;
    row?: string | null;
    split_type?: string | null;
    allow_partial_purchase?: boolean;
  }): Promise<any> {
    const res = await authenticatedFetch(`${BASE_URL}/listings/${id}`, {
      method: 'PATCH',
      headers: await getHeaders(true),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || err.message || `Failed to update listing: ${res.status}`);
    }
    return res.json();
  },

  // ── CrowdVolt ─────────────────────────────────────────────────────────────

  async getCrowdVoltStatus(): Promise<any> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/status`, { headers: await getHeaders(false) });
    if (!res.ok) throw new Error(`Failed to fetch CrowdVolt status: ${res.status}`);
    return res.json();
  },

  async crowdVoltSendOTP(phone: string): Promise<{ method_id: string }> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/connect/send-otp`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ phone }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to send code, try again');
    }
    return res.json();
  },

  async crowdVoltConnectSession(payload: {
    session_token: string;
    session_jwt?: string;
    user_id?: string;
    email?: string;
    phone?: string;
    score?: string;
  }): Promise<any> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/connect/session`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to connect CrowdVolt session');
    }
    return res.json();
  },

  async crowdVoltVerifyOTP(code: string): Promise<any> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/connect/verify-otp`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ code }),
    });
    if (!res.ok) {
      const status = res.status;
      const err = await res.json().catch(() => ({}));
      if (status === 401) throw Object.assign(new Error('Invalid code, please try again'), { status: 401 });
      if (status === 404) throw Object.assign(new Error('Code expired, please restart'), { status: 404 });
      throw new Error(err.error || err.detail || 'Verification failed');
    }
    return res.json();
  },

  async disconnectCrowdVolt(): Promise<{ ok: boolean; message: string }> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/disconnect`, {
      method: 'DELETE',
      headers: await getHeaders(false),
    });
    if (!res.ok) throw new Error(`Failed to disconnect CrowdVolt: ${res.status}`);
    return res.json();
  },

  async getCrowdVoltEvents(
    params?: { include_unlinked?: boolean }
  ): Promise<{ results: any[]; count?: number; success?: boolean }> {
    const qs = new URLSearchParams();
    if (params?.include_unlinked) qs.set('include_unlinked', 'true');
    const url = `${BASE_URL}/crowdvolt/events${qs.toString() ? `?${qs}` : ''}`;
    const res = await authenticatedFetch(url, { headers: await getHeaders(false) });
    if (!res.ok) {
      const status = res.status;
      if (status === 401) throw Object.assign(new Error('reconnect'), { status: 401 });
      throw new Error(`Failed to fetch CrowdVolt events: ${status}`);
    }
    const payload = await res.json().catch(() => ({}));
    const results = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    return {
      ...payload,
      results,
      count: payload?.count,
      success: payload?.success,
    };
  },

  async getCrowdVoltListings(
    params?: { limit?: number; status?: string; ft_event_id?: number; sync?: boolean }
  ): Promise<{ results: any[]; count?: number; success?: boolean }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    if (params?.ft_event_id) qs.set('ft_event_id', String(params.ft_event_id));
    qs.set('sync', String(params?.sync ?? true));
    const url = `${BASE_URL}/crowdvolt/listings${qs.toString() ? `?${qs}` : ''}`;
    const res = await authenticatedFetch(url, { headers: await getHeaders(false) });
    if (!res.ok) {
      const status = res.status;
      if (status === 401) throw Object.assign(new Error('reconnect'), { status: 401 });
      if (status === 502) throw Object.assign(new Error('CrowdVolt unavailable, try again'), { status: 502 });
      throw new Error(`Failed to fetch CrowdVolt listings: ${status}`);
    }
    const payload = await res.json().catch(() => ({}));
    const results = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    return {
      ...payload,
      results,
      count: payload?.count,
      success: payload?.success,
    };
  },

  async linkCrowdVoltListing(ask_uqid: string, ft_listing_id: number, event_uqid: string): Promise<any> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/listings/${ask_uqid}/link`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ ft_listing_id, event_uqid }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to link listing');
    }
    return res.json();
  },

  async unlinkCrowdVoltListing(ask_uqid: string): Promise<any> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/listings/${ask_uqid}/link`, {
      method: 'DELETE',
      headers: await getHeaders(false),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to unlink listing');
    }
    return res.json();
  },

  async updateCrowdVoltListing(ask_uqid: string, data: { price?: number; qty?: number }): Promise<{ ok: boolean; ask_uqid: string; price: number; qty: number }> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/listings/${ask_uqid}`, {
      method: 'PATCH',
      headers: await getHeaders(true),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const status = res.status;
      const err = await res.json().catch(() => ({}));
      if (status === 401) throw Object.assign(new Error('CrowdVolt session expired, please reconnect'), { status: 401 });
      if (status === 502) throw Object.assign(new Error('CrowdVolt unavailable, try again'), { status: 502 });
      throw new Error(err.error || err.detail || 'Failed to update CrowdVolt listing');
    }
    return res.json();
  },

  async searchCrowdVoltEvents(q: string): Promise<{ events: Array<{ event_uqid: string; name: string; date?: string; venue?: string; image?: string }> }> {
    const url = `${BASE_URL}/crowdvolt/events/search?q=${encodeURIComponent(q)}`;
    const res = await authenticatedFetch(url, { headers: await getHeaders(false) });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (res.status === 401) throw Object.assign(new Error('CrowdVolt not connected'), { status: 401 });
      throw new Error(err.error || `CrowdVolt search failed (${res.status})`);
    }
    return res.json();
  },

  async getCrowdVoltEventTicketTypes(event_uqid: string): Promise<{
    event_uqid: string;
    event_name?: string;
    venue?: string;
    ticket_types: Array<{ ticket_type_uqid: string; name: string; price_floor?: number }>;
  }> {
    const url = `${BASE_URL}/crowdvolt/events/${encodeURIComponent(event_uqid)}/ticket-types`;
    const res = await authenticatedFetch(url, { headers: await getHeaders(false) });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Failed to load ticket types (${res.status})`);
    }
    return res.json();
  },

  async createDualListing(payload: {
    ticket_id: number;
    event_uqid: string;
    ticket_type_uqid: string;
    ticket_type_name: string;
    price: number;
    qty: number;
    preferred_wallet_uqid?: string;
    ignore_low_price?: boolean;
  }): Promise<{
    success: boolean;
    ft_listing_id: number;
    cv_ask_uqid: string;
    cv_event_uqid: string;
    price: string;
    qty: number;
  }> {
    const res = await authenticatedFetch(`${BASE_URL}/crowdvolt/listings/create-dual`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      // Surface the wallet-ambiguity case as a structured error so the modal can re-prompt.
      const err: any = new Error(body.error || body.details || `Listing creation failed (${res.status})`);
      err.status = res.status;
      err.body = body;
      throw err;
    }
    return body;
  },

  // ── Dice Subscription ───────────────────────────────────────────────────

  async diceCreateCheckout(
    forwardingEmail: string,
    diceEmail?: string | null,
    profile?: { firstName?: string; lastName?: string; dob?: string },
  ): Promise<{
    checkout_url: string;
    session_id: string;
    subscription_id: number;
    resumed: boolean;
    forwarding_email?: string;
    dice_email?: string;
    dice_first_name?: string;
    dice_last_name?: string;
  }> {
    const body: Record<string, any> = {
      forwarding_email: forwardingEmail,
      success_url: `${window.location.origin}/dice/success?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/dice/signup`,
    };
    if (diceEmail !== undefined) body.dice_email = diceEmail;
    if (profile?.firstName !== undefined) body.dice_first_name = profile.firstName;
    if (profile?.lastName !== undefined) body.dice_last_name = profile.lastName;
    if (profile?.dob !== undefined) body.dice_dob = profile.dob;
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/checkout/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to create checkout session');
    }
    return res.json();
  },

  async diceCancelPending(subscriptionId: number): Promise<{ message: string; subscription_id: number }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/subscription/cancel-pending/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ subscription_id: subscriptionId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to cancel pending session');
    }
    return res.json();
  },

  async diceGetSubscriptions(): Promise<{ subscriptions: DiceSubscription[] }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/subscription/`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch Dice subscriptions: ${res.status}`);
    }
    const data = await res.json();
    return { subscriptions: data.subscriptions ?? [] };
  },

  async diceCancelSubscription(subscriptionId: number, immediate: boolean = false): Promise<{ message: string; status: string; cancel_at_period_end: boolean; current_period_end: string }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/subscription/cancel/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ subscription_id: subscriptionId, immediate }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to cancel subscription');
    }
    return res.json();
  },

  async diceResubscribe(subscriptionId: number): Promise<{ message: string; status?: string; requires_new_checkout?: boolean }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/subscription/resubscribe/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ subscription_id: subscriptionId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (err.requires_new_checkout) {
        return { message: err.error, requires_new_checkout: true };
      }
      throw new Error(err.error || err.detail || 'Failed to resubscribe');
    }
    return res.json();
  },

  async diceUpdateEmail(subscriptionId: number | string, forwardingEmail: string): Promise<{ message: string; forwarding_email: string }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/subscription/update-email/`, {
      method: 'PATCH',
      headers: await getHeaders(true),
      body: JSON.stringify({ subscription_id: subscriptionId, forwarding_email: forwardingEmail }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to update forwarding email');
    }
    return res.json();
  },

  async diceRequestOTP(
    subscriptionId: number | string,
    forwardingEmail: string,
    diceEmail?: string,
    profile?: { firstName?: string; lastName?: string; dob?: string },
  ): Promise<{ message: string; phone_number: string }> {
    const body: Record<string, any> = { subscription_id: subscriptionId, forwarding_email: forwardingEmail };
    if (diceEmail !== undefined) body.dice_email = diceEmail;
    if (profile?.firstName !== undefined) body.dice_first_name = profile.firstName;
    if (profile?.lastName !== undefined) body.dice_last_name = profile.lastName;
    if (profile?.dob !== undefined) body.dice_dob = profile.dob;
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/auto-signup/request-otp/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to request OTP');
    }
    return res.json();
  },

  async diceCompleteSignup(
    subscriptionId: number | string,
    otp: string,
    email?: string,
    profile?: { firstName?: string; lastName?: string; dob?: string },
  ): Promise<{ message: string; connection_id: number; phone_number: string; email: string }> {
    const body: Record<string, any> = { subscription_id: subscriptionId, otp };
    if (email) body.email = email;
    if (profile?.firstName !== undefined) body.first_name = profile.firstName;
    if (profile?.lastName !== undefined) body.last_name = profile.lastName;
    if (profile?.dob !== undefined) body.dob = profile.dob;
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/auto-signup/complete/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to complete signup');
    }
    return res.json();
  },

  async diceBulkCreate(quantity: number, forwardingEmail?: string): Promise<BulkDiceJob> {
    const body: Record<string, any> = { quantity };
    if (forwardingEmail) body.forwarding_email = forwardingEmail;
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/bulk-create/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to start bulk creation');
    }
    return res.json();
  },

  async diceBulkJobs(): Promise<{ jobs: BulkDiceJob[] }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/bulk-create/`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch bulk jobs: ${res.status}`);
    }
    return res.json();
  },

  async diceBulkJobStatus(jobId: number): Promise<BulkDiceJob> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/bulk-create/${jobId}/`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Failed to fetch bulk job ${jobId}`);
    }
    return res.json();
  },

  async diceBulkCancel(jobId: number): Promise<BulkDiceJob> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/bulk-create/${jobId}/cancel/`, {
      method: 'POST',
      headers: await getHeaders(true),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to cancel bulk job');
    }
    return res.json();
  },

  async diceBillingPortal(returnUrl: string): Promise<{ portal_url: string }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/billing-portal/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ return_url: returnUrl }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to open billing portal');
    }
    return res.json();
  },

  async diceGetMyNumbers(): Promise<{ numbers: DicePhoneNumber[] }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/phone-numbers/my-numbers/`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch phone numbers: ${res.status}`);
    }
    return res.json();
  },

  async diceGetMySMS(limit: number = 20): Promise<{ logs: DiceSMSLog[] }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/phone-numbers/my-sms/?limit=${limit}`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch SMS logs: ${res.status}`);
    }
    return res.json();
  },

  async diceSearchNumbers(areaCode: string): Promise<{ numbers: AvailablePhoneNumber[] }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/phone-numbers/numbers/search/?area_code=${encodeURIComponent(areaCode)}`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to search numbers');
    }
    return res.json();
  },

  async getDropConnections(): Promise<{
    user_drop_enabled: boolean;
    connections: Array<{
      id: number;
      phone: string;
      email: string;
      is_active: boolean;
      failed_refresh: boolean;
      is_banned: boolean;
      dice_drop_enabled: boolean;
      tier_match: string | null;
      last_synced_at: string | null;
      /** Account-group tags on this row. Absent on older backends. */
      group_ids?: number[];
    }>;
  }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/drop-connections/`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `Failed to load drop accounts: ${res.status}`);
    }
    return res.json();
  },

  async setDropConnectionEnabled(connectionId: number, enabled: boolean): Promise<any> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/drop-connections/${connectionId}/`, {
      method: 'PATCH',
      headers: await getHeaders(true),
      body: JSON.stringify({ dice_drop_enabled: enabled }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to update drop account');
    }
    return res.json();
  },

  async setDropConnectionTier(connectionId: number, tierMatch: string | null): Promise<any> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/drop-connections/${connectionId}/`, {
      method: 'PATCH',
      headers: await getHeaders(true),
      body: JSON.stringify({ tier_match: tierMatch }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to update tier');
    }
    return res.json();
  },

  async setAllDropConnectionsEnabled(enabled: boolean): Promise<{ success: boolean; enabled: boolean; updated: number }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/drop-connections/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ enabled }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to bulk update drop accounts');
    }
    return res.json();
  },

  /**
   * Set one ticket tier across every drop-enabled DICE account.
   *
   * Same endpoint as the enable/disable bulk toggle — it takes `enabled`,
   * `tier_match`, or both. `null` clears the per-account tier so those accounts
   * fall back to the drop-wide Match Tier at fire time.
   */
  async setAllDropConnectionsTier(
    tierMatch: string | null,
  ): Promise<{ success: boolean; tier_match: string | null; tier_updated: number }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/drop-connections/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ tier_match: tierMatch }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to bulk update ticket tier');
    }
    return res.json();
  },

  async getDiceDropLink(): Promise<{ url: string; expires_at: string }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/integrations/dice/drop-link/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to open DICE drop');
    }
    return res.json();
  },

  async dicePurchaseNumber(phoneNumber: string, forwardingEmail: string): Promise<{ message: string; number: DicePhoneNumber }> {
    const res = await authenticatedFetch(`${INTEGRATIONS_BASE_URL}/phone-numbers/numbers/purchase/`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ phone_number: phoneNumber, forwarding_email: forwardingEmail }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || 'Failed to purchase number');
    }
    return res.json();
  },

  // ── Waitlist ──────────────────────────────────────────────────────────────

  async getSoldoutEvents(params?: {
    waitlist?: boolean;
    subscribed?: boolean;
    city?: string;
    limit?: number;
  }): Promise<SoldoutEvent[]> {
    const queryParams = new URLSearchParams();
    if (params?.waitlist) queryParams.append('waitlist', 'true');
    if (params?.subscribed) queryParams.append('subscribed', 'true');
    if (params?.city) queryParams.append('city', params.city);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const url = queryParams.toString()
      ? `${BASE_URL}/events/soldout?${queryParams.toString()}`
      : `${BASE_URL}/events/soldout`;

    const res = await authenticatedFetch(url, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch sold-out events: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();
    return data.data || [];
  },

  /**
   * Events whose presale or general onsale has not opened yet.
   *
   * Rows arrive sorted by the earliest moment each event becomes buyable — the
   * presale if it has one, the general onsale otherwise. The first row is the
   * next thing to go ON SALE, not the next show to happen, so callers must not
   * re-sort by event_start_date without meaning to.
   *
   * Only well-formed params are sent. The API matches booleans against the
   * literal "true"/"false" and silently ignores anything else — `presale=1` or
   * `limit=0` would quietly return the UNFILTERED set rather than erroring, so
   * a bad value here would look like a backend bug.
   */
  async getUpcomingSales(params?: UpcomingSalesParams): Promise<UpcomingSaleEvent[]> {
    const queryParams = new URLSearchParams();
    if (params?.presale) queryParams.append('presale', 'true');
    if (typeof params?.locked === 'boolean') {
      queryParams.append('locked', params.locked ? 'true' : 'false');
    }
    if (params?.city) queryParams.append('city', params.city);
    if (params?.subscribed) queryParams.append('subscribed', 'true');
    if (params?.limit && Number.isInteger(params.limit) && params.limit > 0) {
      queryParams.append('limit', String(params.limit));
    }

    const url = queryParams.toString()
      ? `${BASE_URL}/events/upcoming-sales?${queryParams.toString()}`
      : `${BASE_URL}/events/upcoming-sales`;

    const res = await authenticatedFetch(url, { headers: await getHeaders() });

    if (!res.ok) {
      // Failures carry {error} with no `success` and no `data`, so the status is
      // the only reliable signal. 503/504 mean the upstream Go service is down
      // or slow rather than anything the caller did wrong.
      const err = await res.json().catch(() => ({} as any));
      throw Object.assign(
        new Error(err.error || err.detail || `Failed to fetch upcoming sales: ${res.status}`),
        { status: res.status },
      );
    }

    const data = await res.json();
    // `data` is already normalized server-side from the upstream's empty-case
    // null, but an empty list is the NORMAL answer here, not a fault.
    return data.data || [];
  },

  // ── Waitlist Status ───────────────────────────────────────────────────
  // Reads our stored copy of Dice's waitlists — no Dice call happens here.
  // The data refreshes via the existing account sync endpoints.

  async getWaitlistEntries(params?: WaitlistEntriesParams): Promise<WaitlistEntriesResponse> {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    if (params?.include_past) qs.append('include_past', 'true');
    if (params?.date_from) qs.append('date_from', params.date_from);
    if (params?.date_to) qs.append('date_to', params.date_to);
    if (params?.account_id != null) qs.append('account_id', String(params.account_id));
    if (params?.event_id) qs.append('event_id', params.event_id);
    if (params?.group_by) qs.append('group_by', params.group_by);

    const url = qs.toString()
      ? `${BASE_URL}/waitlist/entries?${qs.toString()}`
      : `${BASE_URL}/waitlist/entries`;

    const res = await authenticatedFetch(url, { headers: await getHeaders() });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `Failed to fetch waitlist entries: ${res.status}`);
    }

    const body = await res.json();
    // Callers need `sync` to tell the three empty states apart, so the whole
    // envelope is returned rather than just `data`.
    return {
      ...body,
      data: body.data ?? [],
      sync: body.sync ?? {
        last_attempted_at: null,
        last_complete_at: null,
        stale_account_count: 0,
        stale_account_ids: [],
        account_count: 0,
      },
    };
  },

  /**
   * Every eligible Dice account for one event, with the tickets it already
   * holds and its waitlist entry if any — the account picker's whole list.
   *
   * event_id is required; omitting it is a 400.
   */
  async getWaitlistEventStatus(eventId: string): Promise<WaitlistEventStatusResponse> {
    const res = await authenticatedFetch(
      `${BASE_URL}/waitlist/event-status?event_id=${encodeURIComponent(eventId)}`,
      { headers: await getHeaders() },
    );

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `Failed to fetch waitlist status: ${res.status}`);
    }

    const body = await res.json();
    return {
      ...body,
      // active_ticket_types is defaulted rather than typed optional: a response
      // from before it shipped should read as "no breakdown", not blow up the
      // badge that maps over it.
      accounts: (body.accounts ?? []).map((account: any) => ({
        ...account,
        active_ticket_types: account.active_ticket_types ?? [],
      })),
      sync: body.sync ?? {
        last_attempted_at: null,
        last_complete_at: null,
        stale_account_count: 0,
        stale_account_ids: [],
        account_count: 0,
      },
    };
  },

  async joinWaitlist(request: JoinWaitlistRequest): Promise<any> {
    const res = await authenticatedFetch(`${BASE_URL}/waitlist/join`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (res.status === 429) {
        throw new Error('Rate limited — please wait 30 seconds before trying again');
      }
      throw new Error(err.error || err.detail || `Failed to join waitlist: ${res.status}`);
    }

    return res.json();
  },

  /**
   * Claims a Dice waitlist code for one account.
   *
   * Synchronous, unlike bulk-join: the response IS the result, so the caller
   * spins the button until it returns rather than polling a job. `account_id`
   * may only be omitted when the customer has exactly one Dice account.
   */
  async claimWaitlistCode(request: ClaimWaitlistCodeRequest): Promise<ClaimWaitlistCodeResponse> {
    const res = await authenticatedFetch(`${BASE_URL}/waitlist/claim-code`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      // A Cloudflare or gateway failure on the way to Dice isn't JSON, so the
      // parse has to be allowed to fail without swallowing the status.
      const err = await res.json().catch(() => ({} as any));
      throw new WaitlistClaimError(
        err.error || err.detail || `Failed to claim code: ${res.status}`,
        res.status,
      );
    }

    return res.json();
  },

  // ── Bulk Waitlist Join ─────────────────────────────────────────────────

  async bulkJoinWaitlist(request: BulkJoinWaitlistRequest): Promise<BulkJoinResponse> {
    const res = await authenticatedFetch(`${BASE_URL}/waitlist/bulk-join`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (res.status === 429) {
        throw new Error(err.error || 'Rate limited — please wait before trying again');
      }
      throw new Error(err.error || err.detail || `Failed to bulk join waitlist: ${res.status}`);
    }

    return res.json();
  },

  async getBulkJoinJobs(): Promise<BulkJoinJob[]> {
    const res = await authenticatedFetch(`${BASE_URL}/waitlist/bulk-join`, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch bulk join jobs: ${res.status}`);
    }

    return res.json();
  },

  async getBulkJoinJob(jobId: number): Promise<BulkJoinJob> {
    const res = await authenticatedFetch(`${BASE_URL}/waitlist/bulk-join/${jobId}/status`, {
      headers: await getHeaders(),
    });

    if (!res.ok) {
      if (res.status === 404) {
        throw new Error('Job not found');
      }
      throw new Error(`Failed to fetch bulk join job: ${res.status}`);
    }

    const job = await res.json();
    if (job?.items) return job;

    // The /status route answers with counts only. The per-account breakdown —
    // which the picker needs to know WHICH accounts got on the list, not just
    // how many — is on the documented detail route, so fall through to it.
    // Failure here is not fatal: the counts alone still drive the banner.
    try {
      const detail = await authenticatedFetch(
        `${BASE_URL}/waitlist/bulk-join?job_id=${jobId}`,
        { headers: await getHeaders() },
      );
      if (detail.ok) {
        const body = await detail.json();
        const found = Array.isArray(body) ? body.find((j: any) => j?.job_id === jobId) : body;
        if (found?.items) return { ...job, ...found };
      }
    } catch {
      // Keep the status payload — a missing breakdown degrades the badges, not
      // the job itself.
    }

    return job;
  },

  /**
   * Cancel a running bulk-join job. The banner polls once a second and the
   * job can finish between the poll that still said "processing" and the
   * click: the API then answers 400 with the job's real status. That is not
   * a failure — there is simply nothing left to cancel — so it resolves to
   * the finished job instead of throwing (one user saw "Cannot cancel job
   * with status completed" as a red toast on 2026-10-14).
   */
  async cancelBulkJoinJob(jobId: number): Promise<BulkJoinJob> {
    const res = await authenticatedFetch(`${BASE_URL}/waitlist/bulk-join/${jobId}/cancel`, {
      method: 'POST',
      headers: await getHeaders(true),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (res.status === 400 && isFinishedJobStatus(err?.status)) {
        return api.getBulkJoinJob(jobId);
      }
      throw new Error(err.error || err.detail || `Failed to cancel job: ${res.status}`);
    }

    return res.json();
  },

  // ─────────────────────────────────────
  // Subscription billing
  // ─────────────────────────────────────

  async getPlans(): Promise<{ plans: FlashtoolsPlan[] }> {
    const res = await fetch(`${BASE_URL}/billing/plans`, {
      headers: { accept: '*/*' },
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch plans: ${res.status}`);
    }
    return res.json();
  },

  async getEntitlement(): Promise<FlashtoolsEntitlement> {
    const res = await authenticatedFetch(`${BASE_URL}/billing/entitlement`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch entitlement: ${res.status}`);
    }
    return res.json();
  },

  // ── Seats ("chairs") ─────────────────────────────────────────────────────
  // Owner-only. A chair gets 403 from all four, which is why the nav never
  // shows them the Seats screen — but the refusal is the backend's, not this
  // file's.

  async getSeats(): Promise<SeatListResponse> {
    const res = await authenticatedFetch(`${BASE_URL}/seats`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch seats: ${res.status}`);
    }
    return res.json();
  },

  /**
   * Add a chair. An empty `password` means "email them an invite" — the
   * default path, and what the form submits when the field is left blank.
   */
  async createSeat(
    email: string,
    password: string,
    label?: string,
  ): Promise<CreateSeatResponse> {
    const res = await authenticatedFetch(`${BASE_URL}/seats`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ email, password: password || '', label: label || '' }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body?.error || body?.email?.[0] || 'Could not add the chair');
    }
    return body;
  },

  async removeSeat(seatId: number): Promise<{ message: string; billing_warning?: string }> {
    const res = await authenticatedFetch(`${BASE_URL}/seats/${seatId}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body?.error || 'Could not remove the chair');
    }
    return body;
  },

  async resendSeatInvite(
    seatId: number,
  ): Promise<{ message: string; invite_sent: boolean }> {
    const res = await authenticatedFetch(`${BASE_URL}/seats/${seatId}/invite`, {
      method: 'POST',
      headers: await getHeaders(),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body?.error || 'Could not send the invite');
    }
    return body;
  },

  async setSeatPassword(seatId: number, password: string): Promise<{ message: string }> {
    const res = await authenticatedFetch(`${BASE_URL}/seats/${seatId}/password`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ password }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body?.error || 'Could not update the password');
    }
    return body;
  },

  async getUsageDetail(): Promise<FlashtoolsUsageDetail> {
    const res = await authenticatedFetch(`${BASE_URL}/billing/usage-detail`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch usage detail: ${res.status}`);
    }
    return res.json();
  },

  async startCheckout(
    tier: FlashtoolsPlan['tier'],
    successUrl: string,
    cancelUrl: string,
    interval: 'month' | 'year' = 'month',
  ): Promise<CheckoutResponse> {
    const res = await authenticatedFetch(`${BASE_URL}/billing/checkout`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ tier, success_url: successUrl, cancel_url: cancelUrl, interval }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `Failed to start checkout: ${res.status}`);
    }
    return res.json();
  },

  async openBillingPortal(returnUrl: string): Promise<PortalResponse> {
    const res = await authenticatedFetch(`${BASE_URL}/billing/portal`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ return_url: returnUrl }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `Failed to open portal: ${res.status}`);
    }
    return res.json();
  },

  async changePlan(
    tier: FlashtoolsPlan['tier'],
    interval: 'month' | 'year' = 'month',
    returnUrl?: string,
  ): Promise<ChangePlanResponse> {
    const res = await authenticatedFetch(`${BASE_URL}/billing/change-plan`, {
      method: 'POST',
      headers: await getHeaders(true),
      body: JSON.stringify({ tier, interval, return_url: returnUrl }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // Switching plans mid-trial bills immediately, so it needs a chargeable
      // card. The backend answers that case with somewhere to go rather than a
      // dead end; keep it a distinct error type so callers can send the user
      // there instead of showing them a message they can't act on.
      if (err.needs_payment_method) {
        throw new PaymentMethodRequiredError(
          err.error || 'A payment method is required to change plans.',
          err.url,
        );
      }
      throw new Error(err.error || err.detail || `Failed to change plan: ${res.status}`);
    }
    return res.json();
  },

  // Cancelling a trial stops it converting to paid: access continues until
  // trial_end and nothing is charged. resumeTrial undoes it before then.
  async cancelTrial(reason?: string): Promise<TrialActionResponse> {
    return postTrialAction('cancel-trial', reason ? { reason } : {});
  },

  async resumeTrial(): Promise<TrialActionResponse> {
    return postTrialAction('resume-trial', {});
  },

  async getUpcomingInvoice(): Promise<{ invoice: UpcomingInvoice | null }> {
    const res = await authenticatedFetch(`${BASE_URL}/billing/upcoming-invoice`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to load upcoming invoice: ${res.status}`);
    }
    return res.json();
  },

  async getInvoices(limit: number = 12): Promise<{ invoices: InvoiceHistoryItem[] }> {
    const res = await authenticatedFetch(`${BASE_URL}/billing/invoices?limit=${limit}`, {
      headers: await getHeaders(),
    });
    if (!res.ok) {
      throw new Error(`Failed to load invoices: ${res.status}`);
    }
    return res.json();
  },
};

async function postTrialAction(path: 'cancel-trial' | 'resume-trial', body: object): Promise<TrialActionResponse> {
  const res = await authenticatedFetch(`${BASE_URL}/billing/${path}`, {
    method: 'POST',
    headers: await getHeaders(true),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || `Request failed: ${res.status}`);
  }
  return res.json();
}
